/**
 * The card itself: the artwork, and the fields drawn over it.
 *
 * ⚠️ **PORTED FROM `components/BibPassTemplate.tsx` ON GITHUB `main`** (lesson
 * L64). Three things are deliberately NOT carried over, because the server now
 * does them (`server/src/runnercard/card-page.ts`):
 *
 *   - resolving each field's text (`dataSources`, `valueTemplate`, `customText`,
 *     the `N/A` rule) - it arrives resolved;
 *   - deciding which fields are drawn at all - a hidden or empty field is simply
 *     not on the answer;
 *   - picking the template - the rules ran on the server.
 *
 * What is left is drawing, fitting, and being photographable. That is the whole
 * of this file.
 *
 * TWO POSITIONING MODES, AND WHY.
 *
 * On the page a field sits at a PERCENTAGE of the card, so it follows the
 * artwork at any width. For the capture the same field is placed in PIXELS, with
 * a correction subtracted, because html2canvas draws text lower than the browser
 * does (`capture-text-offset.ts`). The switch is `isCapturing`, and
 * `onCaptureReady` fires in the same commit the pixel positions land - so a
 * capture that waits on it can never photograph the uncorrected layout.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { CardPage, CardPageField } from "../api.js";
import { getCaptureTextOffsetForElement } from "./capture-text-offset.js";
import { createCanvasMeasurer, fitField, type FitResult } from "./fit.js";
import { CARD_DEFAULT_FAMILY, loadCardFonts } from "./fonts.js";

/**
 * The card's own width. Every position in a template is a percentage of it, and
 * every `toFitWidth` is in these pixels - so the card is laid out at this width
 * and SCALED to the screen, never re-laid-out narrower.
 */
export const CARD_WIDTH = 450;
/**
 * The artwork's corner radius. Anything drawn behind it must match, and the
 * capture cuts its corners to it (`capture.ts`).
 */
export const CARD_CORNER_RADIUS = 20;
/** How far `row_no` slides up when there is no `row` above it. */
const ROW_EMPTY_OFFSET = 12;

/** Keys the card draws as something other than text. */
const QR_KEY = "qr_code";
const PHOTO_KEY = "profile_picture";
const IMAGE_KEY = "custom_image";

export interface CardTemplateProps {
  card: CardPage;
  /** A data URL for the QR image. Empty until it has been generated. */
  qrCodeUrl: string;
  /** The runner's own photo. Its presence also switches to the cut-out artwork. */
  photoUrl?: string | undefined;
  /**
   * The admin editor only (RC-1.10): a stand-in drawn in an EMPTY photo slot, so
   * the slot can be seen and dragged. Unlike `photoUrl` it does not switch the
   * artwork - the old editor's `showEmptyPhotoSlot`. The runner's card never
   * passes it.
   */
  emptyPhotoSlotUrl?: string | undefined;
  /** Hands the outer element to the capture. */
  containerRef?: (element: HTMLDivElement | null) => void;
  isCapturing?: boolean;
  /** Fires once the capture-mode pixel positions are on the elements. */
  onCaptureReady?: () => void;
}

/** A picture: `custom_image`, or a field drawn as a picture chosen by its value (the flag, G6b). */
function isImageField(field: CardPageField): boolean {
  return field.key === IMAGE_KEY || field.imageUrl !== undefined;
}

function isTextField(field: CardPageField): boolean {
  return field.key !== QR_KEY && field.key !== PHOTO_KEY && !isImageField(field);
}

/**
 * A capture position. `corner` means `left`/`top` are the element's top-left
 * corner and it is drawn with no transform; otherwise they are the point the
 * element's own `translate` centres it on.
 */
interface PixelPosition {
  left: number;
  top: number;
  corner?: boolean;
}

export function CardTemplate({
  card,
  qrCodeUrl,
  photoUrl,
  emptyPhotoSlotUrl,
  containerRef,
  isCapturing = false,
  onCaptureReady,
}: CardTemplateProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [pixelPositions, setPixelPositions] = useState<Record<string, PixelPosition>>({});
  const [photoSizes, setPhotoSizes] = useState<Record<string, { width: number; height: number }>>({});
  /**
   * True once every face this card names has loaded (or failed, or timed out).
   * The fit below measures text, so a face that swaps in afterwards changes the
   * metrics under the measurement - the same trap the BIB display board hit.
   * See `fonts.ts` for why this is not `document.fonts.ready`.
   */
  const [fontsLoaded, setFontsLoaded] = useState(false);

  useEffect(() => {
    if (containerRef) containerRef(rootRef.current);
  }, [containerRef]);

  useEffect(() => {
    let cancelled = false;
    setFontsLoaded(false);
    void loadCardFonts(card.fields.filter(isTextField)).then(() => {
      if (!cancelled) setFontsLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [card.fields]);

  /** Every field's final size and text, worked out once per card. */
  const fitted = useMemo(() => {
    const result = new Map<string, FitResult>();
    for (const field of card.fields) {
      if (!isTextField(field)) continue;
      const measurer = createCanvasMeasurer(field.fontFamily ?? CARD_DEFAULT_FAMILY, field.fontWeight);
      result.set(field.id, fitField(field, measurer));
    }
    return result;
    // `fontsLoaded` is a dependency on purpose: the measurement is only right
    // once the real faces are loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card.fields, fontsLoaded]);

  /** True when the card has a `row` field, which decides where `row_no` sits. */
  const hasRow = useMemo(() => card.fields.some((f) => f.key === "row"), [card.fields]);

  /**
   * The vertical shift a field gets before any capture correction.
   *
   * One rule, and it is a LAYOUT rule rather than a capture correction: `row_no`
   * slides up into the gap when there is no `row` above it. The preview applies
   * the same shift, so both modes agree.
   */
  const layoutShiftOf = useCallback(
    (field: CardPageField): number => (field.key === "row_no" && !hasRow ? ROW_EMPTY_OFFSET : 0),
    [hasRow],
  );

  /**
   * Pixel positions for the capture.
   *
   * `useLayoutEffect`, and measured straight away. This used to sit behind
   * `setTimeout(200)` and two `requestAnimationFrame`s, which raced the
   * capture's own fixed wait and lost whenever frames were slow or not delivered
   * at all - a hidden tab, a backgrounded webview, a busy phone. The capture
   * then photographed the card with these corrections missing and every field
   * came out 10-20px low. `offsetWidth`/`offsetHeight` are layout values and are
   * readable synchronously here, so there is nothing to wait for.
   */
  useLayoutEffect(() => {
    if (!isCapturing) {
      setPixelPositions({});
      return;
    }
    const measureFrom = (width: number, height: number): void => {
      const positions: Record<string, PixelPosition> = {};
      for (const field of card.fields) {
        const left = (field.x / 100) * width;
        let top = (field.y / 100) * height - layoutShiftOf(field);
        const element = rootRef.current?.querySelector(`[data-field-id="${field.id}"]`) ?? null;
        // Pull text up by however far html2canvas is about to push it down,
        // measured from the field as actually rendered - so it follows the real
        // font and whatever size a shrink-to-fit field settled on.
        if (isTextField(field)) top -= getCaptureTextOffsetForElement(element);
        // A picture is centred on its point by `translate(-50%, -50%)` on the
        // page. For the capture it gets its top-left corner instead and no
        // transform: html2canvas places a plain box exactly, while a saved card
        // from a phone (tester, 2026-10-06) drew the transformed flag ~8px left
        // of where the page showed it. `offset*` sizes ignore transforms. A
        // picture with no size yet (still loading) keeps its transform.
        if ((isImageField(field) || field.key === PHOTO_KEY) && element instanceof HTMLElement) {
          const w = element.offsetWidth;
          const h = element.offsetHeight;
          if (w > 0 && h > 0) {
            positions[field.id] = { left: left - w / 2, top: top - h / 2, corner: true };
            continue;
          }
        }
        positions[field.id] = { left, top };
      }
      setPixelPositions(positions);
    };

    const width = rootRef.current?.offsetWidth ?? 0;
    const height = rootRef.current?.offsetHeight ?? 0;
    if (width > 0 && height > 0) {
      measureFrom(width, height);
      return;
    }
    // No size yet - the artwork is still loading. Retry on a TIMER, never on a
    // frame callback, for the reason above.
    const retry = window.setTimeout(() => {
      const w = rootRef.current?.offsetWidth ?? 0;
      const h = rootRef.current?.offsetHeight ?? 0;
      if (w > 0 && h > 0) measureFrom(w, h);
    }, 100);
    return () => window.clearTimeout(retry);
    // `photoSizes`: the photo's box changes when its natural size arrives.
  }, [isCapturing, card.fields, layoutShiftOf, fitted, photoSizes]);

  // Tell the capture the moment those positions are on the elements. Same
  // commit, before paint.
  useLayoutEffect(() => {
    if (!isCapturing || onCaptureReady === undefined) return;
    if (card.fields.length === 0 || Object.keys(pixelPositions).length >= card.fields.length) onCaptureReady();
  }, [isCapturing, pixelPositions, card.fields, onCaptureReady]);

  const background = photoUrl !== undefined && card.backgroundImageUrlWithPhoto !== undefined
    ? card.backgroundImageUrlWithPhoto
    : card.backgroundImageUrl;

  function positionOf(field: CardPageField): { left: string; top: string; centre: string } {
    const pixel = isCapturing ? pixelPositions[field.id] : undefined;
    // How a picture is centred on its point: a transform on the page, nothing
    // once the capture has placed its corner.
    const centre = pixel?.corner === true ? "none" : "translate(-50%, -50%)";
    if (pixel !== undefined) return { left: `${pixel.left}px`, top: `${pixel.top}px`, centre };
    const shift = layoutShiftOf(field);
    return {
      left: `${field.x}%`,
      top: shift === 0 ? `${field.y}%` : `calc(${field.y}% - ${shift}px)`,
      centre,
    };
  }

  return (
    <div
      ref={rootRef}
      // The card is a picture, not text to read (tester, 2026-10-26):
      // - `translate="no"` + `notranslate` on the ROOT, so the artwork is covered
      //   too - Chrome also translates text inside images, and turned the
      //   artwork's "PUMA FAST TRACK" into "FUMA ...".
      // - `text-size-adjust: 100%` here, not only on <body>: a browser's text
      //   zoom (Chrome on iOS sets it on the page) grew the card's text past the
      //   artwork. The page around the card still translates and zooms.
      translate="no"
      className="notranslate"
      // The family is set HERE rather than left to the page, because a field
      // with none of its own is measured in `CARD_DEFAULT_FAMILY` and must be
      // drawn in it too - whatever the page around the card says.
      style={{
        width: `${CARD_WIDTH}px`,
        position: "relative",
        margin: "0 auto",
        color: "#1f2937",
        fontFamily: `"${CARD_DEFAULT_FAMILY}", sans-serif`,
        WebkitTextSizeAdjust: "100%",
        textSizeAdjust: "100%",
      }}
    >
      <div style={{ overflow: "hidden", width: "100%" }}>
        {background !== undefined ? (
          <img
            src={background}
            alt=""
            style={{
              width: "100%",
              height: "auto",
              display: "block",
              pointerEvents: "none",
              borderRadius: `${CARD_CORNER_RADIUS}px`,
              border: "transparent",
            }}
          />
        ) : (
          <div style={{ height: "600px", width: "100%", background: card.backgroundColor ?? "#e5e7eb" }} />
        )}
      </div>

      {/* `translate="no"`: a browser's page translation would rewrite a runner's
          own name, and the card is the one place that must say what the start
          list says. */}
      <div style={{ position: "absolute", inset: 0, overflow: "visible" }} translate="no">
        {card.fields.map((field) => {
          const { left, top, centre } = positionOf(field);

          if (field.key === QR_KEY) {
            return (
              <div
                key={field.id}
                style={{
                  position: "absolute",
                  left,
                  top,
                  width: field.width !== undefined ? `${field.width}%` : "auto",
                  transform: "translate(-50%, -50%)",
                }}
              >
                {qrCodeUrl !== "" && (
                  <img src={qrCodeUrl} alt="QR" style={{ width: `${field.fontSize * 4}px`, height: "auto" }} />
                )}
              </div>
            );
          }

          // `custom_image`, or a picture chosen by the value (the flag, G6b).
          if (field.key === IMAGE_KEY || field.imageUrl !== undefined) {
            // No `crossOrigin` on purpose: the artwork does not set one either,
            // and html2canvas (`useCORS: true`) applies it to its own clone at
            // capture time. Setting it here would break plain display on any
            // host without CORS headers.
            return (
              <img
                key={field.id}
                data-field-id={field.id}
                src={field.imageUrl}
                alt=""
                style={{
                  position: "absolute",
                  left,
                  top,
                  transform: centre,
                  width: field.imageWidth !== undefined ? `${field.imageWidth}px` : "auto",
                  height: field.imageHeight !== undefined ? `${field.imageHeight}px` : "auto",
                  objectFit: "contain",
                  pointerEvents: "none",
                }}
              />
            );
          }

          if (field.key === PHOTO_KEY) {
            // An empty slot draws nothing. A grey stand-in belongs in the
            // template editor, where seeing the slot is the point; on a runner's
            // card it has nothing to show through the artwork but its corners.
            const slotPhoto = photoUrl ?? emptyPhotoSlotUrl;
            if (slotPhoto === undefined) return null;
            const slotWidth = field.profileWidth ?? 100;
            const slotHeight = field.profileHeight ?? 100;
            const circle = (field.profileShape ?? "circle") === "circle";
            // Cover the slot by sizing the photo ourselves. `object-fit` does
            // this in one line on screen, but html2canvas does not implement it
            // - it stretches the photo to the slot instead, so the saved card
            // came out distorted while the page looked fine.
            const natural = photoSizes[field.id];
            const scale =
              natural !== undefined && natural.width > 0 && natural.height > 0
                ? Math.max(slotWidth / natural.width, slotHeight / natural.height)
                : 0;
            return (
              <img
                key={field.id}
                data-field-id={field.id}
                src={slotPhoto}
                alt=""
                onLoad={(event) => {
                  const element = event.currentTarget;
                  const size = { width: element.naturalWidth, height: element.naturalHeight };
                  setPhotoSizes((previous) => {
                    const current = previous[field.id];
                    if (current?.width === size.width && current.height === size.height) return previous;
                    return { ...previous, [field.id]: size };
                  });
                }}
                style={{
                  position: "absolute",
                  left,
                  top,
                  transform: centre,
                  width: `${scale > 0 ? natural!.width * scale : slotWidth}px`,
                  height: `${scale > 0 ? natural!.height * scale : slotHeight}px`,
                  maxWidth: "none",
                  // Behind the artwork on purpose: the photo shows through the
                  // cut-out, so the artwork keeps its logo and text bar on top.
                  // What trims the photo is that cut-out, never a clipping box -
                  // html2canvas mis-renders an image nested in an
                  // `overflow: hidden` wrapper.
                  zIndex: -1,
                  borderRadius: circle ? "50%" : "40px",
                }}
              />
            );
          }

          const fit = fitted.get(field.id);
          const fontSize = fit?.fontSize ?? field.fontSize;
          const text = fit?.text ?? field.text;
          const wrapping = fit?.wrapWidth !== undefined;
          const right = field.toFitType === "fixed";
          const twoLines = text.includes("\n");

          return (
            <div
              key={field.id}
              data-field-id={field.id}
              style={{
                position: "absolute",
                left,
                top,
                fontFamily: field.fontFamily,
                fontSize: `${fontSize}px`,
                // Hollow letters (the BS42 ROW) have no fill: only the outline shows.
                color: field.strokeOnly === true && field.strokeWidth !== undefined ? "transparent" : field.color,
                fontWeight: field.fontWeight,
                textAlign: right ? "right" : (field.textAlign as React.CSSProperties["textAlign"]),
                whiteSpace: wrapping
                  ? "normal"
                  : twoLines
                    ? "pre"
                    : field.toFitType === "scale" || right
                      ? "nowrap"
                      : "pre-wrap",
                // The outline (tester, 2026-10-15) is painted UNDER the fill, so
                // only its outer half shows: twice the width asked for, and the
                // letters keep their own shape. html2canvas 1.4 draws both.
                ...(field.strokeColor !== undefined && field.strokeWidth !== undefined
                  ? field.strokeOnly === true
                    ? // Hollow: no fill, so the whole line shows at its own width.
                      { WebkitTextStroke: `${field.strokeWidth}px ${field.strokeColor}` }
                    : {
                        WebkitTextStroke: `${field.strokeWidth * 2}px ${field.strokeColor}`,
                        paintOrder: "stroke fill",
                      }
                  : {}),
                ...(wrapping
                  ? { maxWidth: `${fit?.wrapWidth}px`, wordBreak: "break-word", overflowWrap: "break-word" }
                  : {}),
                overflow: "visible",
                lineHeight: twoLines ? 1 : 1.2,
                transform: right
                  ? "translate(-100%, -50%)"
                  : field.textAlign === "center"
                    ? "translate(-50%, -50%)"
                    : "translate(0, -50%)",
              }}
            >
              {text}
            </div>
          );
        })}
      </div>
    </div>
  );
}
